import { Link, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { useEvlRun } from './use-evl-intake.js';

const STATUS_LABELS: Record<string, { label: string; tone: 'neutral' | 'warn' | 'ok' | 'bad' }> = {
  running: { label: 'Running', tone: 'neutral' },
  pending_signature: { label: 'Waiting for signature', tone: 'warn' },
  pending_hr_review: { label: 'Held for HR review', tone: 'warn' },
  pending_hr_confirmation: { label: 'Waiting for HR confirmation', tone: 'warn' },
  completed: { label: 'Completed', tone: 'ok' },
  failed: { label: 'Failed', tone: 'bad' },
  cancelled: { label: 'Cancelled', tone: 'bad' },
  rejected: { label: 'Rejected', tone: 'bad' },
};

export function EvlRunStatusPage(): JSX.Element {
  const { companyId, runId } = useParams<{ companyId: string; runId: string }>();
  const { data, isLoading, error } = useEvlRun(companyId, runId);

  if (isLoading) return <p className="text-slate-500">Loading…</p>;
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800">
        <p className="font-medium">Could not load run.</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }
  if (!data) return <p className="text-slate-500">Run not found.</p>;

  const s = STATUS_LABELS[data.status] ?? { label: data.status, tone: 'neutral' as const };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">EVL run</h1>
        <Link
          to={`/companies/${companyId}/modules/evl/runs/new`}
          className="text-sm font-medium text-brand-700 hover:text-brand-800"
        >
          + New run
        </Link>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Run ID</span>
          <span className="font-mono text-xs text-slate-700">{data.id}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Status</span>
          <span
            className={clsx(
              'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
              s.tone === 'ok' && 'bg-green-100 text-green-800',
              s.tone === 'warn' && 'bg-amber-100 text-amber-800',
              s.tone === 'bad' && 'bg-red-100 text-red-800',
              s.tone === 'neutral' && 'bg-slate-100 text-slate-700',
            )}
          >
            {s.label}
          </span>
        </div>
        {data.lastStep && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Last step</span>
            <span className="text-xs text-slate-700">{data.lastStep}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Started</span>
          <span className="text-xs text-slate-700">
            {new Date(data.startedAt).toLocaleString()}
          </span>
        </div>
        {data.completedAt && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">Completed</span>
            <span className="text-xs text-slate-700">
              {new Date(data.completedAt).toLocaleString()}
            </span>
          </div>
        )}
        {data.error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-800">
            {data.error}
          </div>
        )}
      </div>
    </div>
  );
}
